import moment, { Moment } from 'moment';

/*
	The social-event live tags, moved out of Store/actions/SocialEventActions (WEB-11618). The
	function is pure — it never touched Redux — but importing it from there pulled the whole store
	into the importer's module graph, which deadlocked combineReducers when UnlayerMailEditor began
	reaching for it through App/services/MailTags.

	window.Tools.$translate is kept rather than swapped for Components/Helpers/translate: this is a
	move to break that cycle, and re-translating a function the live social-event editor renders
	from is a separate change.
*/

export type LiveTag = {
	name: string;
	value: string;
	html?: boolean;
	isDisabled?: boolean;
	hidden?: boolean;
};

export type LiveTagMap = { [tag: string]: LiveTag };

export type SocialEventLike = {
	date?: string | Date;
	endDate?: string | Date;
	name?: string;
	description?: string;
	location?: string;
	venue?: string;
	time?: string;
	endTime?: string;
	isWebinar?: boolean;
	pageUrl?: string;
};

const getDateTagValue = (
	dateMoment: Moment | null,
	endDateMoment: Moment | null = dateMoment,
	time?: string,
	endTime = ''
): string => {
	if (!dateMoment) {
		return '';
	}
	const dateFormat = 'D MMMM YYYY';
	const startEndSameDay = dateMoment.isSame(endDateMoment ?? undefined, 'day');
	const startEndSameMonth = dateMoment.isSame(endDateMoment ?? undefined, 'month');
	const hasTime = time !== undefined;

	if (startEndSameDay) {
		return dateMoment.format(dateFormat) + (hasTime ? ` ${time} - ${endTime}` : '');
	}

	if (startEndSameMonth && !hasTime) {
		return dateMoment.format(`D - ${endDateMoment!.format('D')} MMMM YYYY`);
	}

	return (
		dateMoment.format(dateFormat) +
		(hasTime ? ' ' + time : '') +
		' - ' +
		endDateMoment!.format(dateFormat) +
		(hasTime ? ' ' + endTime : '')
	);
};

const getDateTagValueNumerical = (
	dateMoment: Moment | null,
	endDateMoment: Moment | null = dateMoment,
	time?: string,
	endTime = ''
): string => {
	if (!dateMoment) {
		return '';
	}
	const dateFormat = 'YYYY-MM-DD';
	const hasTime = time !== undefined;
	if (dateMoment.isSame(endDateMoment ?? undefined, 'day')) {
		return dateMoment.format(dateFormat) + (hasTime ? ` ${time} - ${endTime}` : '');
	}

	return `${dateMoment.format(dateFormat)}${hasTime ? ` ${time}` : ''} - ${endDateMoment!.format(dateFormat)}${
		hasTime ? ' ' + endTime : ''
	}`;
};

export function getLiveTags(event: SocialEventLike = {}, landingpageUrl = event.pageUrl ?? ''): LiveTagMap {
	const dateMoment = event.date ? moment(event.date) : null;
	// `null` would skip the helpers' `= dateMoment` default and compare against now.
	const endDateMoment = event.endDate ? moment(event.endDate) : dateMoment;

	const eventTags: LiveTagMap = {
		'Event.Name': {
			name: window.Tools.$translate('default.name'),
			value: event.name || ''
		},
		'Event.Description': {
			name: window.Tools.$translate('default.description'),
			value: event.description || ''
		},
		'Event.Location': {
			name: window.Tools.$translate('default.location'),
			value: event.location || ''
		},
		'Event.Venue': {
			name: window.Tools.$translate('socialEvent.eventVenue'),
			value: event.venue || ''
		},
		'Event.Date': {
			name: window.Tools.$translate('default.date'),
			value: getDateTagValue(dateMoment, endDateMoment)
		},
		'Event.DateNumerical': {
			name: window.Tools.$translate('socialEvent.dateNumerical'),
			value: getDateTagValueNumerical(dateMoment, endDateMoment)
		},
		'Event.DateAndTime': {
			name: window.Tools.$translate('appointment.dateNTime'),
			value: getDateTagValue(dateMoment, endDateMoment, event.time, event.endTime)
		},
		'Event.DateAndTimeNumerical': {
			name: window.Tools.$translate('socialEvent.dateNTimeNumerical'),
			value: getDateTagValueNumerical(dateMoment, endDateMoment, event.time, event.endTime)
		},
		'Event.EndDate': {
			name: window.Tools.$translate('default.endDate'),
			value: endDateMoment ? endDateMoment.format('D MMMM YYYY') : ''
		},
		'Event.EndDateNumerical': {
			name: window.Tools.$translate('socialEvent.endDateNumerical'),
			value: endDateMoment ? endDateMoment.format('YYYY-MM-DD') : ''
		},
		'Event.StartTime': {
			name: window.Tools.$translate('default.startTime'),
			value: event.time || ''
		},
		'Event.EndTime': {
			name: window.Tools.$translate('default.endTime'),
			value: event.endTime || ''
		},
		'Event.LandingpageUrl': {
			name: window.Tools.$translate('socialEvent.landingpageUrl'),
			value: landingpageUrl
		},
		'Event.DateIso': {
			name: window.Tools.$translate('default.dateISO'),
			value: dateMoment ? dateMoment.format() : ''
		},
		'Event.CalendarLink': {
			name: window.Tools.$translate('socialEvent.calendarLink'),
			value: '[Calendar URL]'
		},
		'Event.CalendarLinkGoogle': {
			name: window.Tools.$translate('socialEvent.calendarLinkGoogle'),
			value: '[Google Calendar URL]'
		}
	};

	const includeWebinarTags = Object.prototype.hasOwnProperty.call(event, 'isWebinar');
	if (includeWebinarTags) {
		const isWebinar = !!event.isWebinar;
		eventTags['Mail.CanNotAttendLink'] = {
			name: window.Tools.$translate('socialEvent.canNotAttendLink'),
			value: `<a href="{{Mail.CanNotAttendLink}}" ng-non-bindable>${window.Tools.$translate(
				'socialEvent.canNotAttend'
			)}</a>`,
			html: true
		};
		eventTags['Mail.WebinarInviteLink'] = {
			name: isWebinar
				? window.Tools.$translate('socialEvent.webinar.link')
				: window.Tools.$translate('socialEvent.webinar.linkPlaceholder'),
			value: '<a href="{{Mail.WebinarInviteLink}}" ng-non-bindable>webinar</a>',
			html: true,
			isDisabled: !isWebinar
		};
		eventTags['Mail.OnDemandLink'] = {
			name: isWebinar
				? window.Tools.$translate('socialEvent.onDemandLink')
				: window.Tools.$translate('socialEvent.webinar.onDemandLinkPlaceholder'),
			value: `<a href="{{Mail.OnDemandLink}}" ng-non-bindable>${window.Tools.$translate(
				'socialEvent.onDemandLink'
			)}</a>`,
			html: true,
			isDisabled: !isWebinar
		};
	}

	return eventTags;
}
